import { DEFAULT_LOCALE, isSupportedLocale, SUPPORTED_LOCALES, type Locale } from "./locales.ts";

export type LocaleSource = "profile" | "cookie" | "accept-language" | "default";

export interface ResolveLocaleInput {
  /** `profiles.locale` — only known after sign-in; wins over everything else when supported. */
  profile?: string | null;
  /** The `fh_locale` cookie (client app) or localStorage value (consultant app). */
  cookie?: string | null;
  /** Raw `Accept-Language` header. */
  acceptLanguage?: string | null;
}

export interface ResolvedLocale {
  locale: Locale;
  source: LocaleSource;
  /** Provided profile/cookie values that are not a supported locale — callers raise `i18n_unsupported_locale`. */
  unsupported: string[];
}

/** Base language of a BCP-47 tag, lowercased: `sv-SE` → `sv`. */
function baseLanguage(tag: string): string {
  return tag.trim().toLowerCase().split("-")[0];
}

/** Language tags from an `Accept-Language` header, highest quality first; `q=0` entries are dropped. */
export function parseAcceptLanguage(header: string | null | undefined): string[] {
  if (!header) return [];
  return header
    .split(",")
    .map((part, index) => {
      const [tag, ...params] = part.trim().split(";");
      const qParam = params.map((p) => p.trim()).find((p) => p.startsWith("q="));
      const q = qParam === undefined ? 1 : Number(qParam.slice(2));
      return { tag: tag.trim(), q, index };
    })
    .filter((entry) => entry.tag !== "" && entry.tag !== "*" && Number.isFinite(entry.q) && entry.q > 0)
    .sort((a, b) => b.q - a.q || a.index - b.index)
    .map((entry) => entry.tag);
}

function matchSupported(tag: string): Locale | undefined {
  const base = baseLanguage(tag);
  return SUPPORTED_LOCALES.find((locale) => locale === base);
}

/**
 * The single resolution order for every runtime: profile → cookie → Accept-Language → default.
 * The client app's per-request path never passes `profile` (the cookie is its cache); the
 * sign-in sync passes both. An unsupported value is skipped, never thrown on.
 */
export function resolveLocale(input: ResolveLocaleInput): ResolvedLocale {
  const unsupported: string[] = [];

  for (const [source, value] of [
    ["profile", input.profile],
    ["cookie", input.cookie],
  ] as const) {
    if (value === undefined || value === null || value === "") continue;
    if (isSupportedLocale(value)) return { locale: value, source, unsupported };
    unsupported.push(value);
  }

  for (const tag of parseAcceptLanguage(input.acceptLanguage)) {
    const locale = matchSupported(tag);
    if (locale) return { locale, source: "accept-language", unsupported };
  }

  return { locale: DEFAULT_LOCALE, source: "default", unsupported };
}
